import moment from 'moment-timezone';
import { SuspensionConfig } from '../@types/common.types';
import { ClaimMdEraStatusCode, SuspensionReason } from '../@types/shared.types';
import {
    ClientSuspensionReason,
    ProviderSuspensionReason,
    SessionType,
    UploadType,
    UserRole,
} from '../enums/shared.enums';
import { fPhone } from '../utils/formatNumber';
import { REACHLINK_CS_PHONE } from './shared.constants';
import { t as translate } from 'i18next';

/**
 * URL param naming the client an admin is acting on behalf of, carried from a
 * client's profile banner into the providers list. Deliberately not `clientId`,
 * which is already in use for other meanings (appointment filters,
 * ProviderClients selection, assignment details).
 */
export const ACTIVE_CLIENT_PARAM = 'activeClientId';

/**
 * URL param naming the provider a booking was started against, carried from
 * the providers list into the booking page. Paired with ACTIVE_CLIENT_PARAM:
 * that one says who you are booking for, this one says with whom.
 */
export const BOOKING_PROVIDER_PARAM = 'bookingProvider';

export const DEFAULT_COUNTRY_CODE = '+1';
export const DEFAULT_COUNTRY = 'US';

// 5 minutes
export const GENERAL_REFRESH_INTERVAL = 5 * 60 * 1000;

// 1 min
export const GENERAL_FAST_REFRESH_INTERVAL = 60 * 1000;

export const DEFAULT_PROVIDER_DAILY_LIMIT = 8;

export const DEFAULT_PROVIDER_PAY_RATE = 5000;
export const DEFAULT_PROVIDER_EAP_FACTOR = 0.75;

export const ADDITIONAL_COUNTRY_CODES = ['+357', '+216', '+30'];

export const GUEST_ROLE_NAME = 'Guest';

export const TRANSCRIPTION_EXPIRATION_DAYS = 30;

export const OLDEST_PERSON = 122;

export const PAYOUT_LAUNCH_DATE = '2024-01-10T00:00:00Z';

export const LOGIN_SEND_CODE_COOLDOWN = 60; // in seconds

export const DIAGNOSIS_NOTES_VISIBLE_WINDOW_MONTHS = 2;

export const DIAGNOSIS_NOTES_TREATMENT_RECOMMENDATION_OPTIONS = [
    'Continue outpatient counseling using EAP sessions',
    'Higher level of care',
];

/**
 * Roles that are presented with the payer experience — payer nav, payer
 * dashboard, payer member list. Presentation only; authorization for these
 * roles differs and is enforced by the backend.
 */
export const PAYER_ROLES = [
    UserRole.PAYER_ADMIN,
    UserRole.NETWORK_PARTNER_ADMIN,
];

/**
 * Internal staff roles used for route gating. NETWORK_PARTNER_ADMIN is
 * deliberately absent: it is an admin on the backend but is presented as a
 * payer, so it takes the PAYER_ROLES routes instead.
 */
export const ADMIN_ROLES = [
    UserRole.SUPER_ADMIN,
    UserRole.BILLING_ADMIN,
    UserRole.CLINICAL_SUPERVISOR,
    UserRole.RECRUITER,
    UserRole.CREDENTIALER,
    UserRole.SCHEDULING_ADMIN,
    UserRole.CC_MANAGER,
    UserRole.CARE_COORDINATOR,
    UserRole.SUPPORT_SPECIALIST,
];

export const FULL_ROLE_TITLES = {
    [UserRole.CLIENT]: 'Client',
    [UserRole.PROVIDER]: 'Provider',
    [UserRole.SHARED]: 'Shared',
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.CLINICAL_SUPERVISOR]: 'Clinical Supervisor',
    [UserRole.BILLING_ADMIN]: 'Billing Admin',
    [UserRole.SCHEDULING_ADMIN]: 'Scheduling Admin',
    [UserRole.RECRUITER]: 'Recruiter',
    [UserRole.CREDENTIALER]: 'Credentialer',
    [UserRole.CARE_COORDINATOR]: 'Care Coordinator',
    [UserRole.SUPPORT_SPECIALIST]: 'Support Specialist',
    [UserRole.CC_MANAGER]: 'CC Manager',
    [UserRole.ORGANIZATION_ADMIN]: 'Organization Admin',
    [UserRole.PAYER_ADMIN]: 'Payer Admin',
    [UserRole.NETWORK_PARTNER_ADMIN]: 'Network Partner Admin',
};

export const AppointmentSettings = {
    FALLBACK_PRICE: 12000,
    CONFIRM_BUFFER: 1440,
    DEFAULT_DURATION: 60,
    TYPE_NAME: 'Appointment',
    PREVIEW_TYPE_NAME: 'AppointmentPreview',
};

export const GeneralAvailabilitySettings = {
    DAYS_AHEAD: 60,
    RESOLUTION: 30,
    START_TIME: '05:00',
    END_TIME: '24:00',
};

export const SpecificAvailabilitySettings = {
    mode: 'multi_select',
    slot_selection: 'unrestricted',
};

export const AvailabilityQuickViewSettings = {
    RESOLUTION: 60,
    mode: 'no_confirm',
};

export const SessionTypePopoverLabels = {
    [SessionType.INDIVIDUAL]: 'Just for me',
    [SessionType.COUPLES]: 'For me and my partner',
    [SessionType.FAMILY]: 'For me and my family',
};

/**
 * Specialty a provider must hold to serve a session type, by name as stored in
 * the `specialties` table. Mirrors the backend's `SESSION_TYPE_SPECIALTIES`.
 *
 * `Individual` is deliberately absent rather than mapped to null: every
 * provider serves it, so it constrains nothing and a lookup returning
 * undefined is exactly the right answer. Partial rather than a full Record so
 * that stays true of any session type added later without a specialty.
 */
export const SessionTypeSpecialtyNames: Readonly<Partial<Record<SessionType, string>>> = {
    [SessionType.COUPLES]: 'Couples Therapy',
    [SessionType.FAMILY]: 'Family Therapy',
};

export const SUSPENSION_SETTINGS: {
    [key in SuspensionReason]: SuspensionConfig;
} = {
    // Providers
    [ProviderSuspensionReason.NO_SHOWS]: {
        title: translate('constants.common.suspension.provider.noShows.title'),
        alert: () => translate('constants.common.suspension.provider.noShows.alert'),
    },
    [ProviderSuspensionReason.INCOMPLETE_NOTES]: {
        title: translate('constants.common.suspension.provider.incompleteNotes.title'),
        alert: () => translate('constants.common.suspension.provider.incompleteNotes.alert'),
    },
    [ProviderSuspensionReason.MONTHLY_REVIEW]: {
        title: translate('constants.common.suspension.provider.monthlyReview.title'),
        alert: () => translate('constants.common.suspension.provider.monthlyReview.alert'),
    },
    [ProviderSuspensionReason.TERMINATION]: {
        title: translate('constants.common.suspension.provider.termination.title'),
        alert: () => translate('constants.common.suspension.provider.termination.alert'),
    },
    [ProviderSuspensionReason.RESIGNATION]: {
        title: translate('constants.common.suspension.provider.resignation.title'),
        alert: () => null,
    },
    [ProviderSuspensionReason.UNFIT_FOR_NEW_CLIENTS]: {
        title: translate('constants.common.suspension.provider.unfitForNewClients.title'),
        alert: () => null,
    },

    // Clients
    [ClientSuspensionReason.OWED_BALANCE]: {
        title: translate('constants.common.suspension.client.owedBalance.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.owedBalance.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'owedBalance',
        },
    },
    [ClientSuspensionReason.TERMED_COVERAGE]: {
        title: translate('constants.common.suspension.client.termedCoverage.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.termedCoverage.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'termedCoverage',
        },
    },
    [ClientSuspensionReason.CLAIM_DENIED]: {
        title: translate('constants.common.suspension.client.claimDenied.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.claimDenied.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'claimDenied',
        },
    },
    [ClientSuspensionReason.LOW_PAYING_PLAN]: {
        title: translate('constants.common.suspension.client.lowPayingPlan.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.lowPayingPlan.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'lowPayingPlan',
        },
    },
    [ClientSuspensionReason.EXPIRED_EAP]: {
        title: translate('constants.common.suspension.client.expiredEap.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.expiredEap.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
    },
    [ClientSuspensionReason.EAP_NO_SHOW]: {
        title: translate('constants.common.suspension.client.eapNoShow.title'),
        alert: () => translate('constants.common.suspension.client.eapNoShow.alert'),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: true,
            copyKey: 'missedSession',
            sessionDetails: true,
        },
    },
    [ClientSuspensionReason.NO_PAYMENT_METHOD]: {
        title: translate('constants.common.suspension.client.noPaymentMethod.title'),
        alert: (user) =>
            translate('constants.common.suspension.client.noPaymentMethod.alert', {
                phone: fPhone(user.country?.liveClientPhoneLine ?? REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'noPaymentMethod',
        },
    },
    [ClientSuspensionReason.NOT_SUITED_FOR_THERAPY]: {
        title: translate('constants.common.suspension.client.notSuitedForTherapy.title'),
        alert: () =>
            translate('constants.common.suspension.client.notSuitedForTherapy.alert', {
                phone: fPhone(REACHLINK_CS_PHONE),
            }),
        blockScheduling: true,
        acknowledgement: {
            unsuspends: false,
            copyKey: 'notSuitedForTherapy',
        },
    },
};

// 10 mb
export const FileSettings = {
    [UploadType.USER_FILE]: {
        // 2MB
        MAX_STORED_FILE_SIZE: 2 * 1024 * 1024,
        // 10 MB
        MAX_INPUT_FILE_SIZE: 10 * 1024 * 1024,
    },
    [UploadType.ORGANIZATION_FILE]: {
        // 2MB
        MAX_STORED_FILE_SIZE: 2 * 1024 * 1024,
        // 10 MB
        MAX_INPUT_FILE_SIZE: 10 * 1024 * 1024,
    },
    [UploadType.DOCUMENT]: {
        // 20MB
        MAX_INPUT_FILE_SIZE: 20 * 1024 * 1024,
    },
    [UploadType.GROUP_FILE]: {
        // 2MB
        MAX_STORED_FILE_SIZE: 2 * 1024 * 1024,
        // 10 MB
        MAX_INPUT_FILE_SIZE: 10 * 1024 * 1024,
    },
};

/** Mirrors the backend's FileSettings for carrier co-branding logos. */
export const CARRIER_LOGO_SETTINGS = {
    MAX_INPUT_FILE_SIZE: 5 * 1024 * 1024,
    ALLOWED_MIME_TYPES: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
};

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

export const MAX_DAILY_LIMIT = 10;

export const MAX_SECONDARY_DX_CODES = 3;

export const SECONDARY_F_CODES = [
    'F06.4',
    'F06.30',
    'F06.31',
    'F06.34',
    'F32.8',
    'F33.2',
    'F33.4',
    'F34.0',
    'F35.89',
    'F39',
    'F41.2',
    'F41.9',
    'F42.8',
    'F42.9',
    'F43.8',
    'F50.2',
    'F63.0',
    'F66',
    'F90.1',
    'F90.2',
    'F90.9',
];

export const COMMON_MENTAL_HEALTH_PROBLEMS = [
    'Depression',
    'Anxiety',
    'Stress',
    'PTSD',
    'Bipolar Disorder',
    'ADHD',
    'OCD',
    'Eating Disorders',
    'Substance Abuse',
    'Schizophrenia',
    'Personality Disorders',
    'Grief',
    'Relationship Issues',
    'Self Esteem',
    'Trauma',
    'Anger Management',
    'Sexual Abuse',
    'Domestic Violence',
    'Domestic Abuse',
    'Sexual Assault',
    'Sexual Harassment',
    'Sexual Violence',
];

/**
 * Suggestions for the provider credentialing degree field, ordered by level. Not exhaustive —
 * the field stays free text so a provider can enter a degree that is not listed.
 */
export const THERAPY_DEGREES = [
    'Bachelor of Arts (BA)',
    'Bachelor of Science (BS)',
    'Bachelor of Social Work (BSW)',
    'Master of Arts (MA)',
    'Master of Science (MS)',
    'Master of Social Work (MSW)',
    'Master of Counseling (MC)',
    'Master of Education (MEd)',
    'Master of Marriage and Family Therapy (MMFT)',
    'Master of Divinity (MDiv)',
    'Master of Public Health (MPH)',
    'Master of Science in Nursing (MSN)',
    'Educational Specialist (EdS)',
    'Doctor of Philosophy (PhD)',
    'Doctor of Psychology (PsyD)',
    'Doctor of Education (EdD)',
    'Doctor of Social Work (DSW)',
    'Doctor of Nursing Practice (DNP)',
    'Doctor of Medicine (MD)',
    'Doctor of Osteopathic Medicine (DO)',
];

export const CLAIM_MD_ERA_STATUSES: {
    code: ClaimMdEraStatusCode;
    description: string;
}[] = [
    {
        code: '1',
        description: translate('constants.common.claimMdEraStatus.1'),
    },
    {
        code: '2',
        description: translate('constants.common.claimMdEraStatus.2'),
    },
    {
        code: '3',
        description: translate('constants.common.claimMdEraStatus.3'),
    },
    {
        code: '4',
        description: translate('constants.common.claimMdEraStatus.4'),
    },
    {
        code: '5',
        description: translate('constants.common.claimMdEraStatus.5'),
    },
    {
        code: '6',
        description: translate('constants.common.claimMdEraStatus.6'),
    },
    {
        code: '7',
        description: translate('constants.common.claimMdEraStatus.7'),
    },
    {
        code: '8',
        description: translate('constants.common.claimMdEraStatus.8'),
    },
    {
        code: '9',
        description: translate('constants.common.claimMdEraStatus.9'),
    },
    {
        code: '10',
        description: translate('constants.common.claimMdEraStatus.10'),
    },
    {
        code: '11',
        description: translate('constants.common.claimMdEraStatus.11'),
    },
    {
        code: '12',
        description: translate('constants.common.claimMdEraStatus.12'),
    },
    {
        code: '13',
        description: translate('constants.common.claimMdEraStatus.13'),
    },
    {
        code: '14',
        description: translate('constants.common.claimMdEraStatus.14'),
    },
    {
        code: '15',
        description: translate('constants.common.claimMdEraStatus.15'),
    },
    {
        code: '16',
        description: translate('constants.common.claimMdEraStatus.16'),
    },
    {
        code: '17',
        description: translate('constants.common.claimMdEraStatus.17'),
    },
    {
        code: '18',
        description: translate('constants.common.claimMdEraStatus.18'),
    },
    {
        code: '19',
        description: translate('constants.common.claimMdEraStatus.19'),
    },
    {
        code: '20',
        description: translate('constants.common.claimMdEraStatus.20'),
    },
    {
        code: '21',
        description: translate('constants.common.claimMdEraStatus.21'),
    },
    {
        code: '22',
        description: translate('constants.common.claimMdEraStatus.22'),
    },
    {
        code: '23',
        description: translate('constants.common.claimMdEraStatus.23'),
    },
    {
        code: '24',
        description: translate('constants.common.claimMdEraStatus.24'),
    },
    {
        code: '25',
        description: translate('constants.common.claimMdEraStatus.25'),
    },
    {
        code: '26',
        description: translate('constants.common.claimMdEraStatus.26'),
    },
    {
        code: '27',
        description: translate('constants.common.claimMdEraStatus.27'),
    },
    {
        code: '28',
        description: translate('constants.common.claimMdEraStatus.28'),
    },
    {
        code: '29',
        description: translate('constants.common.claimMdEraStatus.29'),
    },
    {
        code: '30',
        description: translate('constants.common.claimMdEraStatus.30'),
    },
    {
        code: '31',
        description: translate('constants.common.claimMdEraStatus.31'),
    },
    {
        code: 'AD',
        description: translate('constants.common.claimMdEraStatus.AD'),
    },
    {
        code: 'AP',
        description: translate('constants.common.claimMdEraStatus.AP'),
    },
    {
        code: 'CC',
        description: translate('constants.common.claimMdEraStatus.CC'),
    },
    {
        code: 'CL',
        description: translate('constants.common.claimMdEraStatus.CL'),
    },
    {
        code: 'CP',
        description: translate('constants.common.claimMdEraStatus.CP'),
    },
    {
        code: 'I',
        description: translate('constants.common.claimMdEraStatus.I'),
    },
    {
        code: 'RA',
        description: translate('constants.common.claimMdEraStatus.RA'),
    },
    {
        code: 'RB',
        description: translate('constants.common.claimMdEraStatus.RB'),
    },
    {
        code: 'RC',
        description: translate('constants.common.claimMdEraStatus.RC'),
    },
    {
        code: 'RD',
        description: translate('constants.common.claimMdEraStatus.RD'),
    },
    {
        code: 'RO',
        description: translate('constants.common.claimMdEraStatus.RO'),
    },
];

/* Valid Booking Date Range */
export const VALID_BOOKING_RANGE = {
    start: moment().utc().format(),
    end: moment().add(2, 'month').utc().format(),
};
